import type { QueryClient } from '@tanstack/react-query'
import type { Driver } from 'driver.js'
import type { GlobalTutorialDataKeys } from '../model'
import type { TutorialFeatureFlags } from './tutorialFlags'
import type { TutorialScanPhase } from './tutorialScanOrder'
import type { OrderDetails } from '@/entities/order/model'
import { orderKeys } from '@/entities/order/model'
import { clearZebraOrderScanState, removeZebraEventsForOrder } from '@/features/order/lib'
import {
  mockTutorialSafeArrivedOrders,
  mockTutorialSafeDeliveringOrders,
} from '@/shared/lib/test/mocks/api/dashboardOrders.mocks'
import { stateDefinitionConfigMock } from '@/shared/lib/test/mocks/api/definitions.mocks'
import { mockGroupPartnerOnTheWayOrders, mockOnTheWayOrder } from '@/shared/lib/test/mocks/api/orders.mocks'
import { storeMock } from '@/shared/lib/test/mocks/api/store.mocks'
import {
  buildTutorialDashboardScanOrder,
  buildTutorialFullyScannedOrder,
  buildTutorialScanPhaseOrder,
  TUTORIAL_SCAN_ORDER_ID,
} from './tutorialScanOrder'

/** OTW + partner teaching cards — clear device scan leftovers so CTAs stay Scan 0/7. */
const TUTORIAL_DASHBOARD_SCAN_ORDER_IDS = [TUTORIAL_SCAN_ORDER_ID, '100501', '100502'] as const

function clearTutorialDeviceScanState() {
  for (const orderId of TUTORIAL_DASHBOARD_SCAN_ORDER_IDS) {
    clearZebraOrderScanState(orderId)
    removeZebraEventsForOrder(orderId)
  }
}

/**
 * Force OTW / partner teaching cards back to unscanned teaching state in RQ + __tutorialData.
 * Call after enabling tutorial mode (modal phases / device LS can leave Scanned 7/7 behind).
 */
export function resetTutorialDashboardScanProgress(queryClient: QueryClient): void {
  clearTutorialDeviceScanState()

  const resetOrder = (order: OrderDetails): OrderDetails => {
    if (
      order.orderId === TUTORIAL_SCAN_ORDER_ID
      || order.orderId === '100501'
      || order.orderId === '100502'
    ) {
      return buildTutorialDashboardScanOrder(order)
    }
    return order
  }

  const currentOrders = (queryClient.getQueryData(orderKeys.orders()) as OrderDetails[] | undefined) ?? []
  const nextOrders = currentOrders.map(resetOrder)
  queryClient.setQueryData(orderKeys.orders(), nextOrders)

  if (typeof window !== 'undefined' && window.__tutorialData) {
    window.__tutorialData = {
      ...window.__tutorialData,
      orders: (window.__tutorialData.orders ?? []).map(resetOrder),
    }
  }
}

// Keep PartnerOnTheWay so the card lands in On The Way and partner grouping stays valid.
const stateNames = stateDefinitionConfigMock.states.map((s: { name: string }) => s.name)
const PARTNER_ON_THE_WAY = stateNames.find((s: string) => s === 'PartnerOnTheWay') || stateNames[2]

// Constants for tutorial data
const TUTORIAL_GROUP_ID = 'GRP-20250128-001'
const TUTORIAL_STEP_8_ORDER_ID = TUTORIAL_SCAN_ORDER_ID
const TUTORIAL_PARTNER_DRIVER = 'Alex Martinez'
const TUTORIAL_PARTNER_PICKUP_TIME = mockGroupPartnerOnTheWayOrders[0].partnerPickupTime

// Two partner orders, same driver + pickup window → one grouped Partner card (blue ring).
const TUTORIAL_GROUP_ORDERS = [
  {
    customerName: 'Alex Martinez',
    orderId: '100501',
    carPlate: 'DLV888',
  },
  {
    customerName: 'Blake Chen',
    orderId: '100502',
    carPlate: 'DLV999',
  },
] as const

const TUTORIAL_PARTNER_ORDER_IDS = new Set<string>(
  TUTORIAL_GROUP_ORDERS.map(order => order.orderId),
)

/**
 * Creates a tutorial-safe group order with consistent structure
 */
function createTutorialGroupOrder(orderData: typeof TUTORIAL_GROUP_ORDERS[number]): OrderDetails {
  return {
    ...mockGroupPartnerOnTheWayOrders[0],
    status: PARTNER_ON_THE_WAY,
    isAcknowledged: true,
    customerName: orderData.customerName,
    orderId: orderData.orderId,
    partnerPickupTime: TUTORIAL_PARTNER_PICKUP_TIME,
    hsFields: {
      ...mockGroupPartnerOnTheWayOrders[0].hsFields,
      'orderGroupId': TUTORIAL_GROUP_ID,
      'Order Type': 'Partner Delivery',
      'Customer Name': orderData.customerName,
      'Order Number': orderData.orderId,
      'Driver Name': TUTORIAL_PARTNER_DRIVER,
      ...(orderData.carPlate && { 'Car Plate': orderData.carPlate }),
    },
  }
}

/**
 * Creates the step 8 / scan tutorial order
 */
function createStep8TutorialOrder(): OrderDetails {
  return {
    ...mockOnTheWayOrder,
    customerName: 'Sarah Johnson',
    isAcknowledged: true, // Prevents pulsing animation during tutorial
    hsFields: {
      ...mockOnTheWayOrder.hsFields,
      'Customer Name': 'Sarah Johnson',
      'Order Number': mockOnTheWayOrder.orderId, // Keep the original order ID (100001)
    },
  }
}

/**
 * Gets all tutorial orders (used by multiple handlers)
 * @param zebraBarcodeEnabled - when true, enrich OTW + partner orders with array_barcodes
 */
export function getAllTutorialOrders(zebraBarcodeEnabled = false): OrderDetails[] {
  const tutorialGroupOrders = TUTORIAL_GROUP_ORDERS.map(createTutorialGroupOrder)
  const step8Order = createStep8TutorialOrder()

  const orders = [
    ...tutorialGroupOrders,
    mockTutorialSafeArrivedOrders[0],
    mockTutorialSafeDeliveringOrders[0],
    step8Order,
  ]

  if (!zebraBarcodeEnabled) {
    return orders
  }

  return orders.map((order) => {
    // OTW + partner: teaching cards with licence plates (listing unchanged)
    if (
      order.orderId === TUTORIAL_SCAN_ORDER_ID
      || order.orderId === '100501'
      || order.orderId === '100502'
    ) {
      return buildTutorialDashboardScanOrder(order)
    }
    // Arrived / Delivering: fully accounted so Acknowledge / Complete CTAs stay correct
    if (order.status === 'Arrived' || order.status === 'DeliveringToCustomer') {
      return buildTutorialFullyScannedOrder(order)
    }
    return order
  })
}

function sameTutorialOrderIds(a: OrderDetails[], b: OrderDetails[]): boolean {
  return a.length === b.length && a.every((order, index) => order.orderId === b[index]?.orderId)
}

function writeTutorialOrders(queryClient: QueryClient, orders: OrderDetails[]): void {
  queryClient.setQueryData(orderKeys.orders(), orders)
  if (typeof window !== 'undefined' && window.__tutorialData) {
    window.__tutorialData = {
      ...window.__tutorialData,
      orders,
    }
  }
}

function ordersForMobileDashboardStep(step: number, full: OrderDetails[]): OrderDetails[] {
  if (step === 4) {
    return full.filter(order => order.orderId === TUTORIAL_SCAN_ORDER_ID)
  }
  if (step === 5) {
    return full.filter(order => TUTORIAL_PARTNER_ORDER_IDS.has(order.orderId))
  }
  return full
}

/**
 * Mobile Steps 4–5 cannot scroll the board. Coles stacks Delivering then Arrived
 * above On The Way, so drop every card except the one being taught (Sarah on Step 4,
 * grouped Partner on Step 5). Other steps restore the full set.
 *
 * `tutorialStep` is the Driver.js dashboard index (0 = intro, 4 = Prepare Order, 5 = Partner).
 */
export function syncTutorialMobileDashboardForStep(
  queryClient: QueryClient,
  tutorialStep: number,
  isMobile: boolean,
): boolean {
  if (typeof window === 'undefined' || !window.__tutorialMode) {
    return false
  }

  const zebraBarcodeEnabled = window.__tutorialData?.stateConfig?.features?.zebraBarcodeEnabled ?? false
  const full = getAllTutorialOrders(zebraBarcodeEnabled)
  const next = isMobile ? ordersForMobileDashboardStep(tutorialStep, full) : full

  const current = (queryClient.getQueryData(orderKeys.orders()) as OrderDetails[] | undefined) ?? []
  if (sameTutorialOrderIds(current, next)) {
    return false
  }

  writeTutorialOrders(queryClient, next)
  return true
}

/**
 * Finds an order by token or orderId from tutorial data
 */
export function findTutorialOrder(identifier: string): OrderDetails | undefined {
  const allOrders = getAllTutorialOrders()

  return allOrders.find(order =>
    order.orderId === identifier
    || order.installRef === identifier
    || identifier === TUTORIAL_STEP_8_ORDER_ID,
  ) || createStep8TutorialOrder() // Fallback to step 8 tutorial order
}

/** True while the onboarding walkthrough owns the app's data (mock orders, no real writes). */
export function isTutorialModeActive(): boolean {
  return typeof globalThis.window !== 'undefined' && !!globalThis.window.__tutorialMode
}

/**
 * Enables tutorial mode by setting global window variables.
 * Real backend flags are merged into the mock stateConfig (and orders when Zebra is on).
 */
export async function enableTutorialMode(flags?: Partial<TutorialFeatureFlags>): Promise<boolean> {
  try {
    if (typeof window !== 'undefined') {
      const zebraBarcodeEnabled = flags?.zebraBarcodeEnabled ?? false
      const collectCodeEnabled = flags?.collectCodeEnabled
        ?? stateDefinitionConfigMock.features?.collectCodeEnabled
        ?? false
      const collectTMInitialsEnabled = flags?.collectTMInitialsEnabled ?? false

      // Offline reconcile can re-merge prior scans into RQ; wipe tutorial order IDs first.
      if (zebraBarcodeEnabled) {
        clearTutorialDeviceScanState()
      }

      window.__tutorialMode = true
      window.__tutorialData = {
        orders: getAllTutorialOrders(zebraBarcodeEnabled),
        stateConfig: {
          ...stateDefinitionConfigMock,
          features: {
            ...stateDefinitionConfigMock.features,
            zebraBarcodeEnabled,
            collectCodeEnabled,
            collectTMInitialsEnabled,
          },
        },
        storeDetails: storeMock,
        waitTime: {
          waitTimeAverage: 180, // 3 minutes in seconds
          numberOfOrders: 3,
        },
      }
    }
    return true
  } catch (error) {
    console.error('Failed to enable tutorial mode:', error)
    return false
  }
}

/**
 * Replace the tutorial scan order in React Query + window.__tutorialData for a modal phase.
 */
export function applyTutorialScanPhase(
  queryClient: QueryClient,
  phase: TutorialScanPhase,
): void {
  const phaseOrder = buildTutorialScanPhaseOrder(phase)
  const currentOrders = (queryClient.getQueryData(orderKeys.orders()) as OrderDetails[] | undefined) ?? []
  const nextOrders = currentOrders.map(order =>
    order.orderId === TUTORIAL_SCAN_ORDER_ID ? { ...order, ...phaseOrder, orderId: TUTORIAL_SCAN_ORDER_ID } : order,
  )

  // Ensure the phase order exists even if not already in the list
  if (!nextOrders.some(o => o.orderId === TUTORIAL_SCAN_ORDER_ID)) {
    nextOrders.push(phaseOrder)
  }

  queryClient.setQueryData(orderKeys.orders(), nextOrders)
  if (typeof window !== 'undefined' && window.__tutorialData) {
    window.__tutorialData = {
      ...window.__tutorialData,
      orders: nextOrders,
    }
  }
}

/**
 * Disables tutorial mode by clearing global window variables
 */
export async function disableTutorialMode(): Promise<boolean> {
  try {
    if (typeof window !== 'undefined') {
      clearTutorialDeviceScanState()
      delete window.__tutorialMode
      delete window.__tutorialData
    }
    return true
  } catch (error) {
    console.error('Error disabling tutorial mode:', error)
    return false
  }
}

/**
 * Injects the tutorial header style into the document
 */
export function injectTutorialHeaderStyle(headerHeight: number) {
  if (typeof document !== 'undefined' && !document.getElementById('tutorial-header-style')) {
    const tutorialStyles = document.createElement('style')
    tutorialStyles.id = 'tutorial-header-style'
    tutorialStyles.textContent = `body.tutorial-header-active { padding-top: calc(${headerHeight}px + env(safe-area-inset-top, 0px)); }`
    document.head.appendChild(tutorialStyles)
  }
}

/**
 * Destroys the driver instances safely
 */
export function destroyDriverInstances(driverRef: React.RefObject<Driver | null>, activeDriverRef: React.RefObject<Driver | null>) {
  if (driverRef.current) {
    driverRef.current.destroy()
    driverRef.current = null
  }
  if (activeDriverRef.current && activeDriverRef.current !== driverRef.current) {
    activeDriverRef.current.destroy()
    activeDriverRef.current = null
  }
}

/**
 * Handles tutorial mode logic - returns tutorial data if available, otherwise executes fallback function
 * @param tutorialDataKey - The key of the tutorial data to retrieve
 * @param fallbackFn - The function to execute if not in tutorial mode or data is not available
 * @returns The result of either the tutorial data or the fallback function
 */
export async function withTutorialFallback<T>(
  tutorialDataKey: GlobalTutorialDataKeys,
  fallbackFn: () => Promise<T>,
): Promise<T> {
  // Check if tutorial mode is active
  if (typeof globalThis.window !== 'undefined' && globalThis.window.__tutorialMode) {
    const tutorialData = window.__tutorialData?.[tutorialDataKey] as T | undefined
    if (tutorialData) {
      return tutorialData
    }
  }

  return await fallbackFn()
}
